import { errorMessage } from '../i18n/errorMessages';
import { msg } from '@lingui/core/macro';
import { useState } from 'react';
import * as api from '../lib/api';
import { useToast } from './Toast';
import { RemoteFileViewer } from './RemoteFileViewer';

interface Props {
  path: string;
  agentId: string | null;
}

export default function FileLink({ path, agentId }: Props) {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<api.AgentFileResponse | null>(null);
  const [disabled, setDisabled] = useState(false);
  const { showToast } = useToast();

  const handleClick = async () => {
    if (!agentId || loading || disabled) return;
    setLoading(true);
    try {
      const res = await api.getAgentFile(agentId, path);
      setFile(res);
    } catch (err) {
      const status = err instanceof api.ApiError ? err.status : 0;
      if (status === 503) {
        showToast(msg`The agent is offline. Unable to read the file.`);
        setDisabled(true);
      } else if (status === 504) {
        showToast(msg`File read timed out`);
      } else if (status === 403) {
        showToast(msg`This path is outside the allowed read scope`);
      } else if (status === 404) {
        showToast(msg`File not found`);
      } else if (status === 413) {
        showToast(msg`File is too large to preview`);
      } else {
        showToast(errorMessage(err, msg`Failed to read file`));
      }
    } finally {
      setLoading(false);
    }
  };

  const fileName = path.split('/').pop() ?? path;
  const className = `file-link${loading ? ' file-link-loading' : ''}${disabled ? ' file-link-disabled' : ''}`;

  return (
    <>
      <span
        className={className}
        onClick={handleClick}
        title={path}
      >
        {fileName}
      </span>
      {file && (
        <RemoteFileViewer
          name={fileName}
          content={file.content}
          mimeType={file.mime_type}
          size={file.size}
          onClose={() => setFile(null)}
        />
      )}
    </>
  );
}
